import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import accounting from 'accounting';
import { Menu } from '@headlessui/react';
import moment from 'moment-timezone';
import { findThirdPartyCarriers } from 'utils/UserWebAPIUtils';
import { useTranslation, Trans } from 'react-i18next';
import { titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';
import MultiSelectUser from '../../site/multi-user-search';
import useInterval from '../../../hooks/useInterval';
import CargomaticInput from '../../v2/input';
import AppointmentPicker from './AppointmentPicker';
import Container from '../../v2/container';
import LocationMarkerIcon from '../../v2/icons/LocationMarkerIcon';
import SessionStore from '../../../stores/SessionStore';
import Alert from '../../ui/Alert';
import ProTip from '../../ui/ProTip';
import LoadingCircle from '../../ui/LoadingCircle';
import ChevronLeftIcon from '../../v2/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../v2/icons/ChevronRightIcon';
import { getCircleButtonClass } from '../../v2/buttons/CircleButton';
import DropdownMenu from '../../v2/dropdowns/DropdownMenu';
import DropdownItem from '../../v2/dropdowns/DropdownItem';
import ConfirmModal from '../../ui/modals/ConfirmModal';
import AppointmentStatus from './AppointmentStatus';
import DescriptionListItem from '../../v2/DescriptionListItem';
import { formatDateRange } from '../../../utils/formatters/Date';
import CommentList from '../comments/CommentList';
import CarrierValidationModal from './CarrierValidationModal';

import {
  addComment,
  cancelAppointment,
  createAppointment,
  getAppointment,
  getAppointmentFees,
  getAppointmentSlots,
  getYardByContainer,
  markCommentsAsRead,
  updateAppointment,
  updateAppointmentStatus,
  validateCarrierAppointmentDetails,
  generateStripePaymentLink,
} from '../../../utils/AppointmentWebAPIUtils';

const CANCELED = 'canceled';
const APPROVED = 'approved';
const DECLINED = 'declined';

const confirmMessageMap = {
  [CANCELED]: 'CANCEL_APPOINTMENT_MSG',
  [APPROVED]: 'ACCEPT_APPOINTMENT_MSG',
  [DECLINED]: 'REJECT_APPOINTMENT_MSG',
};

const YARD_NOT_FOUND = 'We could not find a matching yard.';
const CONTAINER_HOLD = 'The container has a hold.';
const PAST_YARD_LFD = 'The appointment is past Yard Last Free Date.';
const CONTAINER_NOT_AVAILABLE =
  'Container is not available at this time. An appointment cannot be requested.';
const CONSIGNEE = 'Consignee';
const CARRIER = 'Carrier';
const ADMIN = 'Admin';

const REFRESH_INTERVAL = 10000;

export default function AppointmentDetails() {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const search = location.search;
  const useQuery = () => new URLSearchParams(search);
  const query = useQuery();
  const [id, setId] = useState();
  const [weekStart, setWeekStart] = useState();
  const [weekEnd, setWeekEnd] = useState();
  const [activeSlot, setActiveSlot] = useState({});
  const [errors, setErrors] = useState({});

  const [carrier, setCarrier] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [standardCarrierAlphaCode, setStandardCarrierAlphaCode] = useState('');
  const [consignee, setConsignee] = useState();
  const [masterBillNumber, setMasterBillNumber] = useState(
    query.get('master_bol') || '',
  );
  const [containerNumber, setContainerNumber] = useState(
    query.get('container') || '',
  );

  const [, setReleaseCode] = useState(query.get('release_code') || '');
  const [appointmentSlots, setAppointmentSlots] = useState([]);
  const [appointmentSlotsError, setAppointmentSlotsError] = useState(false);
  const [yard, setYard] = useState(null);
  const [terminal, setTerminal] = useState(null);
  const [fetchingYard, setFetchingYard] = useState(false);
  const [yardNotFound, setYardNotFound] = useState(false);
  const [containerNotAvailable, setContainerNotAvailable] = useState(false);
  const [hold, setHold] = useState(false);
  const [yardLastFreeDate, setYardLastFreeDate] = useState();
  const [pastYardLastFreeDate, setPastYardLastFreeDate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startedPaymentProcess, setStartedPaymentProcess] = useState(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [showConfirmActionModal, setShowConfirmActionModal] = useState(false);
  const [isThirdPartyCarrier, setIsThirdPartyCarrier] = useState(false);
  const [windowStart, setWindowStart] = useState(
    query.get('window_start') ? moment(query.get('window_start')) : null,
  );
  const [windowEnd, setWindowEnd] = useState(
    query.get('window_end') ? moment(query.get('window_end')) : null,
  );
  const [timezone, setTimezone] = useState();
  const [selectedAction, setSelectedAction] = useState();
  const [status, setStatus] = useState();
  const [confirmMessage, setConfirmMessage] = useState();
  const [showAppointmentSlots, setShowAppointmentSlots] = useState();
  const [comments, setComments] = useState([]);
  const [unreadCommentsCount, setUnreadCommentsCount] = useState(0);
  const [userType, setUserType] = useState(SessionStore.getUserType());
  const [yardAvailableDate, setYardAvailableDate] = useState();

  const [notAuthorized, setNotAuthorized] = useState(false);
  const [showCarrierValidationModal, setShowCarrierValidationModal] =
    useState(false);

  const [pageLoaded, setPageLoaded] = useState(false);

  const [amountDue, setAmountDue] = useState(0);
  const [charges, setCharges] = useState([]);
  const [feesPending, setFeesPending] = useState(true);
  const [currencyCode, setCurrencyCode] = useState(DEFAULT_CURRENCY);
  const [stripePaymentPending, setStripePending] = useState(false);

  function canModifyShipmentFields() {
    if (isEditMode()) {
      return false;
    }
    if (isThirdPartyCarrier) {
      return false;
    }
    return true;
  }

  function canModifyCarrier() {
    if (isThirdPartyCarrier) {
      return false;
    }
    return true;
  }

  function isCarrier() {
    return userType === CARRIER;
  }

  function isAdmin() {
    return userType === ADMIN;
  }

  function isConsignee() {
    return userType === CONSIGNEE;
  }

  function isNewAppointment() {
    return !id;
  }

  function isEditMode() {
    return !!id;
  }

  function canModifyConsignee() {
    if (isNewAppointment() && isAdmin()) {
      return true;
    }
    return false;
  }

  function shouldShowConsignee() {
    if (isAdmin()) {
      return true;
    }
    return false;
  }

  function showComments() {
    return isEditMode();
  }

  function showMarkCommentsAsReadButton() {
    return isEditMode() && isAdmin() && unreadCommentsCount > 0;
  }

  function shouldMarkCommentsAsRead() {
    return unreadCommentsCount > 0 && isEditMode() && !isAdmin();
  }

  function togglePaymentsPolling() {
    const paymentPending = (query.get('payment_pending') || '') === 'true';
    setStripePending(paymentPending);
  }

  function handlePaymentSummaryInterval() {
    if (
      masterBillNumber &&
      containerNumber &&
      windowStart &&
      windowEnd &&
      yard &&
      consignee
    ) {
      fetchAppointmentFees({
        masterBillNumber,
        containerNumber,
        consignee: consignee?.id,
        windowStart,
        windowEnd,
        yard,
      });
    }
  }

  function getStripeRedirectBaseUrl({
    containerNumber,
    masterBillNumber,
    yard,
    windowStart,
    windowEnd,
    consignee,
  }) {
    const baseUrl = window.location.origin;
    const query = qs.stringify({
      container: containerNumber,
      master_bol: masterBillNumber,
      consignee,
      yard,
      windowStart,
      windowEnd,
    });
    return `${baseUrl}/appointments/new?${query}`;
  }

  async function handleGeneratePaymentLink() {
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setStartedPaymentProcess(true);

    const redirectUrl = getStripeRedirectBaseUrl({
      containerNumber,
      masterBillNumber,
      windowStart: windowStart.format(),
      windowEnd: windowEnd.format(),
      yard: yard?._id,
      consignee: consignee?._id,
    });
    const returnUrl = `${redirectUrl})}&payment_pending=true`;
    const cancelUrl = redirectUrl;
    setGeneratingPaymentLink(true);

    const stripePaymentLink = await generateStripePaymentLink({
      container_number: containerNumber,
      master_bill_number: masterBillNumber,
      consignee: consignee?._id,
      window_start: windowStart,
      window_end: windowEnd,
      yard: yard?._id,
      success_url: returnUrl,
      cancel_url: cancelUrl,
    });
    setGeneratingPaymentLink(false);
    window.location.replace(stripePaymentLink);
  }

  useInterval(
    handlePaymentSummaryInterval,
    stripePaymentPending ? REFRESH_INTERVAL : null,
  );

  useEffect(() => {
    handleQueryStringChange();
  }, [location.pathname, search, isThirdPartyCarrier, userType, pageLoaded]);

  useEffect(() => {
    togglePaymentsPolling();
  }, [location.pathname, search]);

  useEffect(() => {
    if (shouldMarkCommentsAsRead()) {
      setTimeout(() => {
        doMarkCommentsAsRead();
      }, 5000);
    }
  }, [unreadCommentsCount]);

  const containerRef = useRef(null);

  async function handleConfirmAction() {
    setShowConfirmActionModal(false);
    setConfirmMessage('');
    if (!selectedAction) {
      return;
    }

    try {
      if (selectedAction === CANCELED) {
        await cancelAppointment(id);
        setStatus(CANCELED);
      } else {
        await updateAppointmentStatus(id, selectedAction);
        setStatus(selectedAction);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSelectedAction(null);
    }
  }

  function toggleAppointmentSlotVisibility() {
    if (id && !isAdmin()) {
      setShowAppointmentSlots(false);
    } else {
      setShowAppointmentSlots(true);
    }
  }

  function handleHideConfirmActionModal() {
    setSelectedAction(null);
    setShowConfirmActionModal(false);
    setConfirmMessage('');
  }

  function handleSelectAction(status) {
    return function () {
      setSelectedAction(status);
      setShowConfirmActionModal(true);
      setConfirmMessage(t(confirmMessageMap[status]));
    };
  }

  async function handleValidateCarrier({
    releaseCode,
    containerNumber,
    masterBillNumber,
  }) {
    const formData = {
      release_code: releaseCode,
      container: containerNumber,
      master_bol: masterBillNumber,
    };

    const {
      found,
      shipment: { consignee },
    } = await validateCarrierAppointmentDetails(formData);
    if (found) {
      setConsignee(consignee);
      setReleaseCode(releaseCode);
      setContainerNumber(containerNumber);
      setMasterBillNumber(masterBillNumber);
      setNotAuthorized(false);
    } else {
      setNotAuthorized(true);
      setShowCarrierValidationModal(true);
    }
  }

  async function handleQueryStringChange() {
    if (!pageLoaded) {
      return;
    }
    if (!userType) {
      return;
    }

    if (isEditMode()) {
      return;
    }

    if (!(isAdmin() || isConsignee() || (isCarrier() && isThirdPartyCarrier))) {
      setNotAuthorized(true);
      return;
    }

    const releaseCode = query.get('release_code') || '';
    const masterBillNumber = query.get('master_bol') || '';
    const containerNumber = query.get('container') || '';

    if (isAdmin() || isConsignee()) {
      setContainerNumber(containerNumber);
      setMasterBillNumber(masterBillNumber);
      return;
    }

    if (
      isThirdPartyCarrier &&
      !(masterBillNumber && containerNumber && releaseCode)
    ) {
      setShowCarrierValidationModal(true);
      return;
    }

    try {
      await handleValidateCarrier({
        releaseCode,
        masterBillNumber,
        containerNumber,
      });
      handleHideCarrierValidationModal();
    } catch (e) {
      console.error(e);
      setShowCarrierValidationModal(true);
    }
  }

  function handleHideCarrierValidationModal() {
    setShowCarrierValidationModal(false);
  }

  function handleSessionChange() {
    const user = SessionStore.getUser();
    const updatedUserType = SessionStore.getUserType();
    setIsThirdPartyCarrier(SessionStore.isThirdPartyCarrier(user));
    setUserType(updatedUserType);

    if (!id && user?._type === CARRIER) {
      const { _id, name, primary_scac_code } = user;
      setCarrier(_id);
      setCarrierName(name);
      setStandardCarrierAlphaCode(primary_scac_code);
    }

    toggleAppointmentSlotVisibility();
    if (updatedUserType === CONSIGNEE) {
      const { _id, company_name, email } = user;
      setConsignee({ _id, company_name, email });
    }
  }

  function getUnreadCommentsCount(comments = []) {
    const otherComments = comments.filter(
      (comment) => userType && userType !== comment.authorType && !comment.read,
    );

    return otherComments.length;
  }

  async function handleCommentAdd(message) {
    try {
      const comments = await addComment(id, message);

      setCommentsState(comments);
    } catch (e) {
      console.error(e);
    }
  }

  const consigneeId = consignee?._id;
  useEffect(() => {
    setWeekStart(moment().startOf('day'));
    setWeekEnd(moment().add(6, 'days').endOf('day'));

    handleSessionChange();
    SessionStore.addChangeListener(handleSessionChange);

    toggleAppointmentSlotVisibility();

    window.scrollTo(0, 0);
    setPageLoaded(true);

    setId(params.id);
    if (params.id) {
      fetchAppointment();
    }

    return () => {
      SessionStore.removeChangeListener(handleSessionChange);
    };
  }, []);

  const yardId = yard?._id;

  useEffect(() => {
    toggleAppointmentSlotVisibility();
  }, [id, userType]);

  useEffect(() => {
    if (yardId && weekStart && weekEnd && timezone) {
      fetchAppointmentSlots();
    }
  }, [yardId, timezone, weekStart, weekEnd]);

  useEffect(() => {
    if (yardAvailableDate) {
      setWeekStart(moment(yardAvailableDate).startOf('day'));
    }
    if (yardLastFreeDate) {
      setWeekEnd(moment(yardLastFreeDate).endOf('day'));
    }
  }, [yardAvailableDate, yardLastFreeDate]);

  async function fetchYard({ consignee, containerNumber }) {
    try {
      setFetchingYard(true);
      setHold(false);
      setPastYardLastFreeDate(false);
      const matchingYard = await getYardByContainer({
        consignee,
        containerNumber,
      });
      if (matchingYard && yard?._id !== matchingYard?._id) {
        setActiveSlot(null);
      }
      if (matchingYard) {
        const { holds, yard_last_free_date, yard_available_date } =
          matchingYard;

        if (!yard_available_date) {
          setContainerNotAvailable(true);
        } else {
          setYard(matchingYard);
          setYardNotFound(false);
          setTimezone(matchingYard.timezone);
          setContainerNotAvailable(false);
          setTerminal(matchingYard.terminal);
          const hasHold = (holds || []).length > 0;

          if (hasHold) {
            setHold(true);
          }

          if (yard_last_free_date) {
            setYardLastFreeDate(yard_last_free_date);
          }
        }
      } else {
        setYardNotFound(true);
      }
    } catch (e) {
      console.log(e);
      setYardNotFound(true);
      setShowAppointmentSlots(false);
    } finally {
      setFetchingYard(false);
    }
  }

  async function fetchAppointment() {
    const { id } = params;
    if (!id) {
      return;
    }
    const appointment = await getAppointment(id);
    const {
      terminal,
      yard,
      container_number: containerNumber,
      master_bill_number: masterBillNumber,
      window_start,
      window_end,
      consignee,
      status,
      comments,
      scac_code,
      carrier,
      yard_available_date,
      yard_last_free_date,
    } = appointment;

    const windowStart = moment(window_start);
    const windowEnd = moment(window_end);

    setStatus(status || 'pending');
    setYard(yard);
    setTerminal(terminal);
    setContainerNumber(containerNumber);
    setMasterBillNumber(masterBillNumber);
    setConsignee(consignee);
    setTimezone(yard.timezone || 'America/Los_Angeles');
    setWindowStart(windowStart);
    setWindowEnd(windowEnd);
    setCarrier(carrier?._id);
    setCarrierName(carrier?.name);
    setStandardCarrierAlphaCode(scac_code || carrier?.primary_scac_code);
    setCommentsState(comments);
    setYardAvailableDate(yard_available_date);
    setYardLastFreeDate(yard_last_free_date);
    setActiveSlot({ windowStart });

    fetchAppointmentFees({
      masterBillNumber,
      containerNumber,
      consignee: consignee?._id,
      windowStart,
      windowEnd,
      yard,
    });
  }

  function handleScrollToComments(e) {
    e.preventDefault();
    handleMarkCommentsAsRead();
    if (containerRef.current) {
      const yOffset = -10;
      const y =
        containerRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  function handleMarkCommentsAsRead() {
    if (isEditMode()) {
      doMarkCommentsAsRead();
    }
  }

  function setCommentsState(incomingComments = []) {
    setComments(incomingComments);
    updateUnreadCommentsCount(incomingComments);
  }

  function updateUnreadCommentsCount(incomingComments) {
    const count = getUnreadCommentsCount(incomingComments);
    setUnreadCommentsCount(count);
  }

  async function fetchAppointmentSlots() {
    let startDate;

    if (!isAdmin() && isEditMode()) {
      return;
    }

    if (isAdmin()) {
      startDate = moment();
    } else {
      startDate = moment().add(1, 'day');
    }
    try {
      const appointmentSlots = await getAppointmentSlots({
        yardId,
        timezone,
        weekStart: startDate.toISOString(),
        weekEnd: weekEnd.toISOString(),
      });

      setAppointmentSlots(appointmentSlots);
      setAppointmentSlotsError(false);
    } catch (e) {
      setAppointmentSlotsError(true);
    }
  }

  function handleSelectAppointment(appointmentSlot) {
    setActiveSlot(appointmentSlot);

    const { windowStart, windowEnd } = appointmentSlot;
    setWindowStart(windowStart);
    setWindowEnd(windowEnd);

    if (isPastYardLastFreeDate(yardLastFreeDate, windowStart)) {
      setPastYardLastFreeDate(true);
    } else {
      setPastYardLastFreeDate(false);
    }

    fetchAppointmentFees({
      masterBillNumber,
      containerNumber,
      consignee: consignee?._id,
      windowStart,
      windowEnd,
      yard,
    });
  }

  async function fetchAppointmentFees({
    masterBillNumber,
    containerNumber,
    consignee,
    windowStart,
    windowEnd,
    yard,
  }) {
    setFeesPending(true);
    try {
      const {
        shipment: { currency },
        adminFeeItem,
        managedReceivables: { charges, total },
      } = await getAppointmentFees({
        consignee,
        container_number: containerNumber,
        master_bill_number: masterBillNumber,
        window_start: windowStart,
        window_end: windowEnd,
        yard: yard?._id,
      });

      if (adminFeeItem) {
        setCharges(charges.concat(adminFeeItem));
      } else {
        setCharges(charges);
      }
      setCurrencyCode(currency || DEFAULT_CURRENCY);
      setAmountDue(total);
      setFeesPending(false);

      if (total === 0) {
        setStripePending(false);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function isPastYardLastFreeDate(yardLastFreeDate, windowStart) {
    if (!yardLastFreeDate || !windowStart) return false;

    const lastMomentOfLFD = moment(yardLastFreeDate).endOf('day');
    const pastLFD = windowStart.isAfter(lastMomentOfLFD);
    return pastLFD;
  }

  function handleSelectCarrier(_value, item) {
    setCarrier(item._id);
    setCarrierName(item.name);
    setStandardCarrierAlphaCode(item.primary_scac_code);
  }

  function handleRemoveCarrier() {
    setCarrier(null);
    setCarrierName('');
    setStandardCarrierAlphaCode('');
  }

  function getSelectedCarriers() {
    if (carrier) {
      return [
        {
          _id: carrier,
          name: carrierName,
          scac_code: standardCarrierAlphaCode,
        },
      ];
    }
    return [];
  }

  function handleStandardCarrierAlphaCodeChange(value) {
    setStandardCarrierAlphaCode(value);
  }

  function handleSelectConsignee(_value, item) {
    setYard(null);
    setConsignee(item);
    setFeesPending(true);
  }

  function handleRemoveConsignee() {
    setYard(null);
    setConsignee(null);
    setFeesPending(true);
  }

  function getSelectedConsignee() {
    if (consignee) {
      return [consignee];
    }
    return [];
  }

  function handleMasterBillNumberChange(value) {
    setMasterBillNumber(value);
    setFeesPending(true);
  }

  function handleContainerNumberChange(value) {
    setContainerNumber(value);
    setYard(null);
    setFeesPending(true);
  }

  function validate() {
    const errors = {};

    if (!carrier) {
      errors.carrier = t('SELECT_A_CARRIER');
    }

    if (!masterBillNumber) {
      errors.masterBillNumber = t('PROVIDE_MASTER_BILL_NUM');
    }

    if (!containerNumber) {
      errors.containerNumber = t('PROVIDE_CONTAINER_NUMBER');
    }

    if (!consignee?._id) {
      errors.consignee = t('SELECT_A_CONSIGNEE');
    }

    if (!windowStart || !windowEnd) {
      errors.appointment = t('SELECT_APPOINTMENT_SLOT');
    }
    return errors;
  }

  function formatDatetime(datetime, timezone) {
    return moment(datetime).seconds(0).tz(timezone, true).format();
  }

  function transformWindow() {
    return {
      window_start: moment.utc(formatDatetime(windowStart, timezone)).format(),
      window_end: moment.utc(formatDatetime(windowEnd, timezone)).format(),
    };
  }

  function transformRequest(status) {
    const request = {
      master_bill_number: masterBillNumber,
      container_number: containerNumber,
      carrier,
      consignee: consignee._id,
      yard: yard._id,
      scac_code: standardCarrierAlphaCode,
      terminal,
      ...transformWindow(),
    };

    if (id) {
      request._id = id;
    }

    if (status) {
      request.status = status;
    }
    return request;
  }

  function handleFindYard() {
    try {
      fetchYard({ consignee: consigneeId, containerNumber });
    } catch (e) {
      console.error(e);
    }
  }

  async function handleCreateAppointment() {
    setSaving(true);
    try {
      const newAppointment = await createAppointment(transformRequest());
      setStatus(newAppointment.status);
      setId(newAppointment._id);
      history.push(`/appointments/${newAppointment._id}`);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateAppointment() {
    setSaving(true);
    try {
      await updateAppointment(id, transformRequest());
    } finally {
      setSaving(false);
    }
  }

  function markCommentsAsReadDisabled() {
    return unreadCommentsCount === 0;
  }

  async function doMarkCommentsAsRead() {
    if (unreadCommentsCount === 0) {
      return;
    }

    const { comments: updatedComments } = await markCommentsAsRead(id);

    setCommentsState(updatedComments);
  }

  function handleSubmit() {
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (isEditMode()) {
      handleUpdateAppointment();
    } else {
      handleCreateAppointment();
    }
  }

  function findYardDisabled() {
    return !containerNumber || !consignee?._id;
  }

  function submitDisabled() {
    return (
      !yard?._id || !windowStart || !windowEnd || feesPending || amountDue > 0
    );
  }

  function renderNotAuthorized() {
    return (
      <Container className="min-h-screen py-10">
        <Alert type="error" title={t('NOT_AUTHORIZED')}>
          {t('UNAUTHORIZED_TO_VIEW_PAGE')}
        </Alert>
      </Container>
    );
  }

  function renderActionsMenu() {
    if (!id) {
      return null;
    }
    if (isConsignee() && (status === CANCELED || status === APPROVED)) {
      return null;
    }

    return (
      <DropdownMenu title={t('ACTIONS')}>
        {SessionStore.isAdmin() && [
          <Menu.Item id="approve" key="approve">
            <DropdownItem onClick={handleSelectAction(APPROVED)}>
              {t('ACCEPT')}
            </DropdownItem>
          </Menu.Item>,
          <Menu.Item key="reject">
            <DropdownItem onClick={handleSelectAction(DECLINED)}>
              {t('REJECT')}
            </DropdownItem>
          </Menu.Item>,
        ]}
        {status !== CANCELED && (
          <Menu.Item key="cancel">
            <DropdownItem onClick={handleSelectAction(CANCELED)}>
              {t('CANCEL')}
            </DropdownItem>
          </Menu.Item>
        )}
      </DropdownMenu>
    );
  }

  function renderProTip() {
    if (isEditMode() || isThirdPartyCarrier) {
      return null;
    }
    let message;
    if (isConsignee()) {
      message = t('PROVIDE_CONSIGNEE_TIP');
    } else {
      message = t('PROVIDE_CONTAINER_NUMBER_TIP');
    }

    return <ProTip className="mb-4">{message}</ProTip>;
  }

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert className="my-4" title={t('ADDRESS_ISSUES')} type="error">
        <ul>
          {Object.values(errors).map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  function renderUnreadCommentsCount() {
    if (isNewAppointment() || !unreadCommentsCount) {
      return null;
    }

    return (
      <Alert type="info" className="my-4">
        <div className="flex justify-between">
          <p className="my-0">
            <Trans
              i18nKey="UNREAD_MESSAGE_WITH_COUNT"
              values={{ unreadCommentsCount }}
              components={[<strong />]}
            />
          </p>
          <a href="#" onClick={handleScrollToComments}>
            <div className="flex items-center font-medium">
              <span>{t('READ_MESSAGES')}</span>
              <ChevronRightIcon />
            </div>
          </a>
        </div>
      </Alert>
    );
  }

  function renderFees() {
    if (feesPending) {
      return null;
    }
    return (
      <div className="">
        <hr />
        <div className="grid sm:grid-cols-3 sm:gap-4">
          <div className="hidden sm:block grid-cols-full sm:col-span-1">
            <div className="flex items-center space-x-2 mb-2">
              <CircleText>3</CircleText>
              <h3 className="my-0 text-gray-500">{t('FEES_DUE')}</h3>
            </div>
          </div>
          <div className="sm:col-span-2">
            {!stripePaymentPending && amountDue === 0 && (
              <Alert type="success" className="mb-2">
                {t('NO_PAYMENTS_REQUIRED')}
              </Alert>
            )}
            {!stripePaymentPending && amountDue > 0 && (
              <div>
                <Alert type="error" className="mb-2">
                  {t('PAY_OUTSTANDING_FEE')}
                </Alert>
                {charges.length > 0 && (
                  <table className="default-table">
                    <thead>
                      <tr>
                        <th>{t('DESCRIPTION', { count: 1 })}</th>
                        <th>{t('UNIT', { count: 2 })}</th>
                        <th>{t('RATE', { count: 1 })}</th>
                        <th>{t('AMOUNT', { count: 1 })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((charge, i) => (
                        <tr className={i} key={i}>
                          <td>
                            <div>{charge.name}</div>
                            {charge.meta?.length > 0 && (
                              <div className="mt-1 grid grid-cols-2 gap-4">
                                {charge.meta.map((item, i) => (
                                  <DescriptionListItem
                                    className="ml-4"
                                    label={item.label}
                                    key={i}
                                  >
                                    {item.formatted_value}
                                  </DescriptionListItem>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>{accounting.formatNumber(charge.units, 2)}</td>
                          <td>{formatMoney(charge.rate, currencyCode)}</td>
                          <td>{formatMoney(charge.amount, currencyCode)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>{t('TOTAL')}</td>
                        <td className="font-medium">
                          {formatMoney(amountDue, currencyCode)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <DescriptionListItem label={t('CONTAINER_NUMBER')}>
                    {containerNumber}
                  </DescriptionListItem>
                  <DescriptionListItem
                    label={t('YARD_WITH_NAME', { name: yard?.name })}
                  >
                    {yard?.address}
                  </DescriptionListItem>
                  <DescriptionListItem
                    label={t('TERMINAL_WITH_NAME', { name: terminal?.name })}
                  >
                    {terminal?.address}
                  </DescriptionListItem>
                </div>

                <p className="mt-2 text-xs mb-1">
                  {t('DEMURRAGE_AND_DETENTION_NOTE')}
                </p>
                <p className="text-xs">
                  {t('DEMURRAGE_AND_DETENTION_INVOICE_NOTE')}
                </p>
                <p className="text-xs">{t('CUSTOMER_SUPPORT_FOR_DISPUTE')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const saveLabel = isEditMode()
    ? t('UPDATE_APPOINTMENT')
    : t('SCHEDULE_APPOINTMENT');

  if (notAuthorized) {
    return renderNotAuthorized();
  }

  return (
    <Container className="min-h-screen py-10">
      <div>
        <div className="flex items-center justify-between -mx-4 mb-4">
          <div className="px-4">
            <Link
              to="/appointments"
              className="text-blue-500 hover:text-blue-700 flex items-center space-x-1 underline"
            >
              <ChevronLeftIcon />
              <span>{t('APPOINTMENTS')}</span>
            </Link>
          </div>
          <div className="px-4 flex items-center space-x-2">
            {status && <AppointmentStatus status={status} />}
            {renderActionsMenu()}
          </div>
        </div>
        {renderUnreadCommentsCount()}
        {stripePaymentPending && (
          <Alert type="info" className="my-4">
            {t('PAYMENT_UNDER_PROCESSING')}
          </Alert>
        )}
        <div className="px-4 py-5 sm:p-6 bg-white rounded-md shadow">
          <div className="grid sm:grid-cols-3 sm:gap-4">
            <div className="hidden sm:block grid-cols-full sm:col-span-1">
              <div className="flex items-center space-x-2">
                <CircleText>1</CircleText>
                <h3 className="my-0 text-gray-500">
                  {t('APPOINTMENT_DETAILS')}
                </h3>
              </div>
            </div>
            <div className="grid-cols-full sm:col-span-2">
              {renderProTip()}
              <fieldset className="mb-4">
                <div className="grid grid-cols-2 gap-4">
                  {!canModifyShipmentFields() && [
                    <DescriptionListItem
                      label={t('CONTAINER_NUMBER')}
                      key="container"
                    >
                      {containerNumber}
                    </DescriptionListItem>,
                    <DescriptionListItem
                      label={t('MASTER_BILL_NUMBER')}
                      key="mbl"
                    >
                      {masterBillNumber}
                    </DescriptionListItem>,
                  ]}
                  {canModifyShipmentFields() && [
                    <CargomaticInput
                      key="containerNumber"
                      label={t('CONTAINER_NUMBER')}
                      placeholder={t('CONTAINER_NUMBER')}
                      onChange={handleContainerNumberChange}
                      value={containerNumber}
                    />,
                    <CargomaticInput
                      key="masterBillNumber"
                      label={t('MASTER_BILL_NUMBER')}
                      placeholder={t('MASTER_BILL_NUMBER')}
                      onChange={handleMasterBillNumberChange}
                      value={masterBillNumber}
                    />,
                  ]}
                </div>
              </fieldset>
              <fieldset className="mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    {!canModifyCarrier() && (
                      <DescriptionListItem
                        label={titleize(t('CARRIER', { count: 1 }))}
                      >
                        {carrierName}
                      </DescriptionListItem>
                    )}
                    {canModifyCarrier() && (
                      <div>
                        <label className="form-label text-gray-700 mb-2">
                          {titleize(t('CARRIER', { count: 1 }))}
                        </label>

                        <MultiSelectUser
                          useNewSearch
                          type="carriers"
                          label={titleize(t('CARRIER', { count: 1 }))}
                          placeholder={t('SEARCH_FOR_CARRIER')}
                          onSelect={handleSelectCarrier}
                          onRemove={handleRemoveCarrier}
                          selectedItems={getSelectedCarriers()}
                          searchCustomers={findThirdPartyCarriers}
                          userSearchTags="Third Party Carrier"
                          multiple={false}
                        />
                      </div>
                    )}
                  </div>
                  <CargomaticInput
                    label={t('CARRIER_SCAC')}
                    placeholder={t('CARRIER_SCAC')}
                    onChange={handleStandardCarrierAlphaCodeChange}
                    value={standardCarrierAlphaCode}
                  />
                </div>
              </fieldset>
              {shouldShowConsignee() && !canModifyConsignee() && (
                <DescriptionListItem
                  label={titleize(t('CONSIGNEE', { count: 1 }))}
                >
                  {consignee?.company_name}
                </DescriptionListItem>
              )}
              {shouldShowConsignee() && canModifyConsignee() && (
                <fieldset className="mb-4">
                  <label className="form-label mb-1">
                    {t('CUSTOMER_INFORMATION')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <MultiSelectUser
                      type="consignees"
                      placeholder={t('SEARCH_FOR_CONSIGNEE')}
                      onSelect={handleSelectConsignee}
                      onRemove={handleRemoveConsignee}
                      selectedItems={getSelectedConsignee()}
                      multiple={false}
                    />
                  </div>
                </fieldset>
              )}
            </div>
          </div>
          {yardNotFound && !containerNotAvailable && (
            <Alert type="error" className="my-4">
              {t('YARD_NOT_FOUND')}
            </Alert>
          )}
          {containerNotAvailable && (
            <Alert type="error" className="my-y">
              {t('CONTAINER_NOT_AVAILABLE')}
            </Alert>
          )}
          {!yard && (
            <div className="flex justify-end">
              <button
                className="button button-green"
                disabled={findYardDisabled()}
                onClick={handleFindYard}
              >
                {!fetchingYard && <span>{t('FIND_YARD')}</span>}
                {fetchingYard && (
                  <LoadingCircle text={`${t('SEARCHING')}...`} />
                )}
              </button>
            </div>
          )}

          {yard && (
            <div>
              <hr />
              <div className="">
                <div className="flex items-center space-x-2 mb-2">
                  <CircleText>2</CircleText>
                  <h3 className="my-0 text-gray-500">
                    <Trans
                      i18nKey="AVAILABLE_APPOINTMENTS_AT_WITH_NAME"
                      values={{ name: yard.name }}
                      components={[<strong className="font-bold" />]}
                    />
                  </h3>
                </div>
                <div className="flex items-center space-x-2 mb-4">
                  <CircleText color="green">
                    <LocationMarkerIcon />
                  </CircleText>
                  <span>{yard.address}</span>
                </div>
                {(hold || pastYardLastFreeDate) && amountDue > 0 && (
                  <Alert
                    type="warning"
                    className="my-4"
                    title={t('PAYMENT_AT_AVAILABLE_EMAIL')}
                  >
                    <ul>
                      {hold && <li>{t('CONTAINER_HOLD')}</li>}
                      {pastYardLastFreeDate && <li>{t('PAST_YARD_LFD')}</li>}
                    </ul>
                  </Alert>
                )}
                <DescriptionListItem
                  label={t('SELECTED_APPOINTMENT')}
                  className="my-4"
                >
                  {formatDateRange(windowStart, windowEnd, timezone)}
                </DescriptionListItem>
                {showAppointmentSlots && (
                  <div>
                    <div className="">
                      {!appointmentSlotsError && (
                        <AppointmentPicker
                          weekStart={weekStart}
                          onSelect={handleSelectAppointment}
                          appointmentSlots={appointmentSlots}
                          activeSlot={activeSlot}
                        />
                      )}
                      {appointmentSlotsError && (
                        <Alert type="error">
                          {t('APPOINTMENT_RETRIEVAL_ERROR')}
                        </Alert>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {renderFees()}
              {renderErrors()}
              <div className="mt-4 flex items-center justify-end bg-gray-50 -mx-4 -mb-5 sm:-mx-6 sm:-mb-6">
                <div className="px-4 py-5 sm:p-6">
                  <Link
                    to="/appointments"
                    className="text-blue-500 hover:text-blue-700 underline flex items-center space-x-1"
                  >
                    <ChevronLeftIcon />
                    <span>{t('BACK')}</span>
                  </Link>
                </div>
                {!stripePaymentPending && !feesPending && amountDue > 0 && (
                  <div className="px-4 py-5 sm:p-6">
                    <button
                      className="button button-red"
                      onClick={handleGeneratePaymentLink}
                    >
                      {generatingPaymentLink && (
                        <LoadingCircle
                          text={`${t('GENERATING_PAYMENT_LINK')}...`}
                        />
                      )}
                      {!generatingPaymentLink && <span>{t('PAY_FEES')}</span>}
                    </button>
                  </div>
                )}
                <div className="px-4 py-5 sm:p-6">
                  <button
                    className="button button-green"
                    disabled={submitDisabled()}
                    onClick={handleSubmit}
                  >
                    {saving && <LoadingCircle>{t('SAVING')}</LoadingCircle>}
                    {!saving && <span>{saveLabel}</span>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {showComments() && (
          <div className="my-6">
            <div className="sm:w-1/2 w-full">
              <div className="grid-cols-full sm:col-span-1">
                <div
                  className="flex items-center space-x-2 mb-3 justify-between"
                  ref={containerRef}
                >
                  <h3 className="my-0 text-gray-500">{t('COMMENTS')}</h3>
                  {showMarkCommentsAsReadButton() && (
                    <button
                      className="button button-hollow-green"
                      type="button"
                      disabled={markCommentsAsReadDisabled()}
                      onClick={handleMarkCommentsAsRead}
                    >
                      {t('MARK_AS_READ')}
                    </button>
                  )}
                </div>
              </div>
              <CommentList
                onCommentAdd={handleCommentAdd}
                comments={comments}
                showEmptyState={false}
              />
            </div>
          </div>
        )}
      </div>
      {showConfirmActionModal && (
        <ConfirmModal
          show={showConfirmActionModal}
          onHide={handleHideConfirmActionModal}
          onConfirm={handleConfirmAction}
          message={confirmMessage}
        />
      )}
      <CarrierValidationModal
        show={showCarrierValidationModal}
        onHideModal={handleHideCarrierValidationModal}
        canCancel={false}
      />
    </Container>
  );
}

const CircleText = ({ children, color }) => (
  <span
    className={getCircleButtonClass({
      color,
      size: 'small',
    })}
  >
    {children}
  </span>
);

CircleText.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  color: PropTypes.string,
};

CircleText.defaultProps = {
  color: 'gray',
};
